import { registerSW } from 'virtual:pwa-register';

import {
  choisirBascule,
  estEchecChargementMorceau,
  estPerimee,
  INTERVALLE_VERIFICATION_MS,
  lienARechargerEnEntier,
  peutRecharger,
  reagirEchecMorceau,
} from './logique.js';
import { saisieEnCours, suivreSaisie } from './saisie.js';
import { VERSION_INTERFACE } from './version.js';

/**
 * Mises à jour de l'interface (révision du référent du 2026-10-31) : enregistrement du service
 * worker, vérification d'une nouvelle version (périodique, au retour sur l'onglet, au retour du
 * réseau) et bascule sans perte de saisie. Une seule fois par page (`demarrerMiseAJour`).
 */

const CLE_RECHARGEMENT = 'oasis.miseajour.rechargement';
/** Garde du rechargement après un morceau introuvable (une seule fois, voir logique.ts). */
const CLE_MORCEAU = 'oasis.miseajour.morceau';

let afficheeDepuis = Date.now();
let enAttente = false;
let masque = false;
let enregistrement: ServiceWorkerRegistration | undefined;
const abonnes = new Set<() => void>();

function prevenir(): void {
  for (const f of abonnes) f();
}

export function abonner(f: () => void): () => void {
  abonnes.add(f);
  return () => {
    abonnes.delete(f);
  };
}

export const nouvelleVersionEnAttente = (): boolean => enAttente;

/** Masque le bandeau ; la navigation suivante charge quand même la nouvelle version. */
export function plusTard(): void {
  masque = true;
  prevenir();
}
export const bandeauVisible = (): boolean => enAttente && !masque;

function lireHeure(cle: string): number | null {
  try {
    const v = Number(window.sessionStorage.getItem(cle));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

const dernierRechargement = (): number | null => lireHeure(CLE_RECHARGEMENT);

/** Recharge la page (la navigation passe par le réseau : nouvelle version). */
export function recharger(): void {
  try {
    window.sessionStorage.setItem(CLE_RECHARGEMENT, String(Date.now()));
  } catch {
    // Stockage indisponible : le rechargement reste possible.
  }
  window.location.reload();
}

/** Affiche le bandeau « Nouvelle version d'Oasis disponible » (même s'il avait été masqué). */
function proposerRechargement(): void {
  enAttente = true;
  masque = false;
  prevenir();
}

/**
 * Un morceau de l'interface est introuvable (page d'avant un déploiement) : rechargement
 * automatique une seule fois, sinon bandeau. Renvoie `true` si la page se recharge.
 */
export function echecChargementMorceau(): boolean {
  const reponse = reagirEchecMorceau({
    enLigne: navigator.onLine,
    saisieEnCours: saisieEnCours(),
    dernierRechargement: lireHeure(CLE_MORCEAU),
    maintenant: Date.now(),
  });
  if (reponse === 'recharger') {
    try {
      window.sessionStorage.setItem(CLE_MORCEAU, String(Date.now()));
      recharger();
      return true;
    } catch {
      // Sans stockage, pas de garde possible : bandeau plutôt qu'un risque de boucle.
    }
  }
  proposerRechargement();
  return false;
}

function etatPage(): Parameters<typeof choisirBascule>[0] {
  return {
    enLigne: navigator.onLine,
    visible: document.visibilityState === 'visible',
    saisieEnCours: saisieEnCours(),
    msDepuisAffichage: Date.now() - afficheeDepuis,
  };
}

/** Une nouvelle version est servie : recharger tout de suite, ou proposer. */
function basculer(): void {
  const choix = choisirBascule(etatPage());
  if (choix === 'recharger' && peutRecharger(dernierRechargement(), Date.now())) {
    recharger();
    return;
  }
  if (!enAttente) {
    enAttente = true;
    prevenir();
  }
}

async function versionServie(): Promise<string | null> {
  try {
    const r = await fetch('/config.json', { cache: 'no-store' });
    if (!r.ok) return null;
    const c = (await r.json()) as { version?: unknown };
    return typeof c.version === 'string' ? c.version : null;
  } catch {
    return null;
  }
}

/** Compare la page à la version servie ; `signalSw` : un nouveau service worker s'est activé. */
async function verifier(signalSw: boolean): Promise<void> {
  if (enAttente) {
    basculer();
    return;
  }
  if (estPerimee(VERSION_INTERFACE, await versionServie(), signalSw)) basculer();
}

function chercherMiseAJour(): void {
  if (!navigator.onLine) return;
  void enregistrement?.update().catch(() => undefined);
  void verifier(false);
}

let demarre = false;

export function demarrerMiseAJour(): void {
  if (demarre) return;
  demarre = true;
  suivreSaisie();

  registerSW({
    immediate: true,
    // Un nouveau service worker vient de prendre la main (version déployée).
    onNeedReload: () => {
      void verifier(true);
    },
    onRegisteredSW: (_url, r) => {
      enregistrement = r;
    },
  });

  window.setInterval(chercherMiseAJour, INTERVALLE_VERIFICATION_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      afficheeDepuis = Date.now();
      chercherMiseAJour();
    } else if (enAttente && !saisieEnCours() && peutRecharger(dernierRechargement(), Date.now())) {
      // Onglet quitté sans saisie : il sera à jour au retour.
      recharger();
    }
  });
  window.addEventListener('online', chercherMiseAJour);

  // Fichier d'un écran introuvable : la page date d'avant un déploiement.
  window.addEventListener('vite:preloadError', (e) => {
    // Page rechargée : inutile de laisser l'erreur s'afficher entre-temps.
    if (echecChargementMorceau()) e.preventDefault();
  });
  // Import dynamique hors du préchargement de Vite : même réponse.
  window.addEventListener('unhandledrejection', (e) => {
    if (estEchecChargementMorceau(e.reason)) echecChargementMorceau();
  });

  // Nouvelle version en attente : le prochain lien suivi charge la page en entier.
  document.addEventListener(
    'click',
    (e) => {
      if (!enAttente || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || saisieEnCours()) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement)) return;
      const lien = {
        href: a.href,
        target: a.getAttribute('target'),
        download: a.hasAttribute('download'),
      };
      if (!lienARechargerEnEntier(lien, window.location.origin)) return;
      e.preventDefault();
      window.location.assign(a.href);
    },
    true,
  );
  window.addEventListener('popstate', () => {
    if (enAttente && !saisieEnCours()) window.location.reload();
  });
}
